/**
 * High quality's ambient occlusion, tuned by eye in the live mockup and on
 * device: a dual-radius SSAO from StarCraft II (Filion & McNaughton,
 * SIGGRAPH 2008). A tight near radius emphasises creases and corners
 * (contact darkening) while a much larger far radius fakes low-frequency
 * diffuse inter-reflection (broad, soft shading). AO is multiplied into the
 * ambient term only, never the key light (see sceneLighting.ts).
 */

export interface SsaoParams {
  /** Opacity of the AO effect: 0 = invisible, 1 = full strength. */
  aoStrength: number;
  /** The near and far sampling radii (world units, feet). */
  nearRadius: number;
  farRadius: number;
  /** How much each radius contributes. */
  nearWeight: number;
  farWeight: number;
  /** Samples taken per radius (so total taps = 2 × this). */
  samples: number;
  /** Contrast curve on the final AO (higher = darker, punchier). */
  power: number;
  /** Depth-difference scale for the range check: stops a far-nearer object over-occluding. */
  rangeFalloff: number;
  /** AO buffer resolution as a fraction of the framebuffer (0.5 = half-res). */
  resScale: number;
}

/**
 * Eight samples per radius halve the AO cost of sixteen with no perceptible
 * quality loss (verified on device), and old fill-rate-bound GPUs feel the
 * difference dramatically.
 */
export const SSAO_PARAMS: SsaoParams = {
  aoStrength: 0.54,
  nearRadius: 0.95,
  farRadius: 3.0,
  nearWeight: 1.05,
  farWeight: 0.6,
  samples: 8,
  power: 1.4,
  rangeFalloff: 3.15,
  resScale: 0.7,
};
